import React, { useState, useEffect } from 'react';
import { FaSave, FaUndo, FaDollarSign, FaBusinessTime } from 'react-icons/fa';
import { fetchAuth } from '../../utils/api';
import './Configuracion.css';

function Configuracion() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [config, setConfig] = useState({
    tarifa_hora_junior: 8000,
    tarifa_hora_semi_senior: 12000,
    tarifa_hora_senior: 18000,
    horas_maximas_semanales: 48,
    horas_minimas_proyecto: 8
  });

  const [originalConfig, setOriginalConfig] = useState({});
  const [tplGeneralName, setTplGeneralName] = useState('');
  const [tplGeneralPendingFile, setTplGeneralPendingFile] = useState(null);
  const [tplAdminName, setTplAdminName] = useState('');
  const [tplAdminPendingFile, setTplAdminPendingFile] = useState(null);
  const [tplColabName, setTplColabName] = useState('');
  const [tplColabPendingFile, setTplColabPendingFile] = useState(null);

  const [tarifas, setTarifas] = useState([]);
  const [tarifasOriginal, setTarifasOriginal] = useState([]);
  const [tarifasSaving, setTarifasSaving] = useState(false);
  const [tarifasError, setTarifasError] = useState('');
  const [confirmTarifasOpen, setConfirmTarifasOpen] = useState(false);

  useEffect(() => {
    fetchConfiguracion();
    fetchTarifas();
  }, []);

  useEffect(() => {
    const loadGeneralTpl = async () => {
      try {
        const resp = await fetchAuth('/api/admin/plantillas/general/', { rawResponse: true });
        if (!resp.ok) return;
        const data = await resp.json();
        setTplGeneralName(String(data.name || ''));
      } catch {}
    };
    loadGeneralTpl();
  }, []);

  useEffect(() => {
    const loadAdminTpl = async () => {
      try {
        const resp = await fetchAuth('/api/admin/plantillas/admin/', { rawResponse: true });
        if (!resp.ok) return;
        const data = await resp.json();
        setTplAdminName(String(data.name || ''));
      } catch {}
    };
    loadAdminTpl();
  }, []);

  useEffect(() => {
    const loadColabTpl = async () => {
      try {
        const resp = await fetchAuth('/api/admin/plantillas/colaborador/', { rawResponse: true });
        if (!resp.ok) return;
        const data = await resp.json();
        setTplColabName(String(data.name || ''));
      } catch {}
    };
    loadColabTpl();
  }, []);

  const fetchConfiguracion = async () => {
    try {
      setLoading(true);
      const data = await fetchAuth('/api/admin/configuracion/');
      setConfig(data);
      setOriginalConfig(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
    setSuccessMessage('');
  };

  const fetchTarifas = async () => {
    try {
      setTarifasError('');
      const data = await fetchAuth('/api/admin/tarifas/');
      setTarifas(data.tarifas || []);
      setTarifasOriginal(JSON.parse(JSON.stringify(data.tarifas || [])));
    } catch (e) {
      setTarifasError(e.message || 'Error al cargar tarifas');
    }
  };

  const addTarifaRow = () => {
    setTarifas(t => ([...t, { id_tarifa: `tmp_${Date.now()}`, nombre_tarifa: '', monto: 0, moneda: 'CLP', _tmp: true }]));
  };

  const updateTarifaField = (id, field, value) => {
    setTarifas(ts => ts.map(t => t.id_tarifa === id ? { ...t, [field]: field === 'monto' ? (parseFloat(value) || 0) : value } : t));
  };

  const removeTarifaRow = (id) => {
    setTarifas(ts => ts.filter(t => t.id_tarifa !== id));
  };

  const hasTarifaChanges = () => JSON.stringify(tarifasOriginal) !== JSON.stringify(tarifas);

  const saveTarifas = () => {
    setConfirmTarifasOpen(true);
  };

  const doSaveTarifas = async () => {
    try {
      setTarifasSaving(true);
      setTarifasError('');

      // Crear
      for (const t of tarifas) {
        if (String(t.id_tarifa).startsWith('tmp_')) {
          await fetchAuth('/api/admin/tarifas/crear/', { method: 'POST', body: JSON.stringify({ nombre_tarifa: t.nombre_tarifa, monto: t.monto, moneda: t.moneda || 'CLP' }) });
        }
      }
      // Actualizar
      for (const t of tarifas) {
        const orig = tarifasOriginal.find(o => o.id_tarifa === t.id_tarifa);
        if (orig && (orig.nombre_tarifa !== t.nombre_tarifa || orig.monto !== t.monto || (orig.moneda||'CLP') !== (t.moneda||'CLP'))) {
          await fetchAuth(`/api/admin/tarifas/${t.id_tarifa}/actualizar/`, { method: 'PUT', body: JSON.stringify({ nombre_tarifa: t.nombre_tarifa, monto: t.monto, moneda: t.moneda || 'CLP' }) });
        }
      }
      // Eliminar (las que estaban y ya no están)
      for (const o of tarifasOriginal) {
        if (!tarifas.find(t => t.id_tarifa === o.id_tarifa)) {
          await fetchAuth(`/api/admin/tarifas/${o.id_tarifa}/eliminar/`, { method: 'DELETE' });
        }
      }

      await fetchTarifas();
      setSuccessMessage('✅ Tarifas guardadas exitosamente');
      setTimeout(() => setSuccessMessage(''), 3000);
      setConfirmTarifasOpen(false);
    } catch (e) {
      setTarifasError(e.message || 'Error al guardar tarifas');
    } finally {
      setTarifasSaving(false);
    }
  };

  const handleGuardar = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setError(null);

      await fetchAuth('/api/admin/configuracion/actualizar/', {
        method: 'PUT',
        body: JSON.stringify(config)
      });

      setOriginalConfig(config);
      setSuccessMessage('✅ Configuración guardada exitosamente');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRestaurar = () => {
    setConfig(originalConfig);
    setSuccessMessage('');
    setError(null);
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig);

  const handleUploadTplGeneral = (file) => {
    if (!file) return;
    setTplGeneralPendingFile(file);
  };

  const handleSaveTplGeneral = () => {
    if (!tplGeneralPendingFile) return;
    const run = async () => {
      try {
        const fd = new FormData();
        fd.append('file', tplGeneralPendingFile);
        const data = await fetchAuth('/api/admin/plantillas/general/', { method: 'POST', body: fd });
        setTplGeneralName(String(data.name || tplGeneralPendingFile.name));
        setTplGeneralPendingFile(null);
        setSuccessMessage('✅ Plantilla de Reporte General guardada');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (e) {
        setError(e.message || 'Error al guardar plantilla');
      }
    };
    run();
  };

  const handleRemoveTplGeneral = () => {
    const run = async () => {
      try {
        await fetchAuth('/api/admin/plantillas/general/', { method: 'DELETE' });
        setTplGeneralName('');
        setTplGeneralPendingFile(null);
        setSuccessMessage('✅ Plantilla de Reporte General eliminada');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (e) {
        setError(e.message || 'Error al eliminar plantilla');
      }
    };
    run();
  };

  const handleUploadTplAdmin = (file) => {
    if (!file) return;
    setTplAdminPendingFile(file);
  };

  const handleSaveTplAdmin = () => {
    if (!tplAdminPendingFile) return;
    const run = async () => {
      try {
        const fd = new FormData();
        fd.append('file', tplAdminPendingFile);
        const data = await fetchAuth('/api/admin/plantillas/admin/', { method: 'POST', body: fd });
        setTplAdminName(String(data.name || tplAdminPendingFile.name));
        setTplAdminPendingFile(null);
        setSuccessMessage('✅ Plantilla de Reporte Pago Vista Administrador guardada');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (e) {
        setError(e.message || 'Error al guardar plantilla');
      }
    };
    run();
  };

  const handleRemoveTplAdmin = () => {
    localStorage.removeItem('config_plantillas_reportes_admin');
    setTplAdminName('');
    setTplAdminPendingFile(null);
    setSuccessMessage('✅ Plantilla de Reporte Pago Vista Administrador eliminada');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleUploadTplColab = (file) => {
    if (!file) return;
    setTplColabPendingFile(file);
  };

  const handleSaveTplColab = () => {
    if (!tplColabPendingFile) return;
    const run = async () => {
      try {
        const fd = new FormData();
        fd.append('file', tplColabPendingFile);
        const data = await fetchAuth('/api/admin/plantillas/colaborador/', { method: 'POST', body: fd });
        setTplColabName(String(data.name || tplColabPendingFile.name));
        setTplColabPendingFile(null);
        setSuccessMessage('✅ Plantilla de Reporte de Pago Colaborador guardada');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (e) {
        setError(e.message || 'Error al guardar plantilla');
      }
    };
    run();
  };

  const handleRemoveTplColab = () => {
    const run = async () => {
      try {
        await fetchAuth('/api/admin/plantillas/colaborador/', { method: 'DELETE' });
        setTplColabName('');
        setTplColabPendingFile(null);
        setSuccessMessage('✅ Plantilla de Reporte de Pago Colaborador eliminada');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (e) {
        setError(e.message || 'Error al eliminar plantilla');
      }
    };
    run();
  };

  if (loading) {
    return (
      <div className="configuracion">
        <div className="loading-spinner">Cargando configuración...</div>
      </div>
    );
  }

  return (
    <div className="configuracion">
      <div className="config-header">
        <div>
          <h1>Configuración del Sistema</h1>
          <p className="config-subtitle">Gestiona las tarifas, plazos y reglas del sistema</p>
        </div>
        
        <div className="config-actions">
          {hasChanges && (
            <button className="btn-restaurar" onClick={handleRestaurar}>
              <FaUndo /> Descartar Cambios
            </button>
          )}
          <button 
            className="btn-guardar-config" 
            onClick={handleGuardar}
            disabled={saving || !hasChanges}
          >
            <FaSave /> {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message-config">
          <p>❌ {error}</p>
        </div>
      )}

      <div className="config-sections">
        {/* Sección de Tarifas */}
        <div className="config-section">
          <div className="section-header">
            <FaDollarSign className="section-icon" />
            <div>
              <h2>Tarifas por Hora</h2>
              <p>Define las tarifas según el nivel de experiencia</p>
            </div>
          </div>

          

          <div className="tarifas-admin">
            <div className="tarifas-admin-header">
              <h3>Gestión de Tarifas (tabla tarifa)</h3>
              <div className="tarifas-admin-actions">
                <button className="btn-restaurar" onClick={addTarifaRow}>Agregar Tarifa</button>
                <button className="btn-guardar-config" onClick={saveTarifas} disabled={tarifasSaving || !hasTarifaChanges()}>
                  <FaSave /> {tarifasSaving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
            {tarifasError && (
              <div className="error-message-config tarifas-error">
                <p>❌ {tarifasError}</p>
                <button className="btn-restaurar" onClick={fetchTarifas} style={{marginTop: '0.5rem'}}>Reintentar</button>
              </div>
            )}
              <div className="tarifas-table">
              <div className="tarifas-row tarifas-head">
                <div>Nombre de Tarifa</div>
                <div>Monto</div>
                <div>Moneda</div>
                <div>Acciones</div>
              </div>
              {tarifas.map(t => (
                <div key={t.id_tarifa} className="tarifas-row">
                  <div>
                    <input type="text" value={t.nombre_tarifa} onChange={(e) => updateTarifaField(t.id_tarifa, 'nombre_tarifa', e.target.value)} placeholder="Nombre de tarifa (ej.: Colaborador Junior)" aria-label="Nombre de tarifa" />
                  </div>
                  <div className="input-group">
                    <span className="input-prefix">$</span>
                    <input type="number" min="0" step="100" value={t.monto} onChange={(e) => updateTarifaField(t.id_tarifa, 'monto', e.target.value)} />
                  </div>
                  <div>
                    <input type="text" value={t.moneda || 'CLP'} onChange={(e) => updateTarifaField(t.id_tarifa, 'moneda', e.target.value)} placeholder="CLP" aria-label="Moneda" />
                  </div>
                  <div>
                    {!String(t.id_tarifa).startsWith('tmp_') && (
                      <button className="btn-eliminar-tarifa" onClick={() => removeTarifaRow(t.id_tarifa)}>Eliminar</button>
                    )}
                    {String(t.id_tarifa).startsWith('tmp_') && (
                      <button className="btn-eliminar-tarifa" onClick={() => removeTarifaRow(t.id_tarifa)}>Quitar</button>
                    )}
                  </div>
                </div>
              ))}
              {tarifas.length === 0 && (
                <div className="no-resultados tarifas-empty">
                  <p>No hay tarifas cargadas</p>
                  <button className="btn-restaurar" onClick={addTarifaRow}>Agregar primera tarifa</button>
                </div>
              )}
            </div>
          </div>
          {confirmTarifasOpen && (
            <div className="confirm-overlay" onClick={() => setConfirmTarifasOpen(false)}>
              <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-header">
                  <h3>Confirmar cambios</h3>
                </div>
                <div className="confirm-body">
                  <p>Se guardarán los cambios en tarifas. ¿Deseas continuar?</p>
                </div>
                <div className="confirm-actions">
                  <button className="btn-restaurar" onClick={() => setConfirmTarifasOpen(false)}>Cancelar</button>
                  <button className="btn-guardar-config" onClick={doSaveTarifas} disabled={tarifasSaving}>
                    <FaSave /> {tarifasSaving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        


        {/* Plantillas de Reporte */}
        <div className="config-section">
          <div className="section-header">
            <div>
              <h2>Plantillas de Reporte</h2>
              <p>Gestiona las plantillas PDF usadas en los exportables</p>
            </div>
          </div>

          <div className="config-grid">
            <div className="config-item">
              <label>Reporte General (PDF)</label>
              <div className="input-group">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleUploadTplGeneral(e.target.files?.[0] || null)}
                />
              </div>
              <div className="template-status" style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {tplGeneralPendingFile && (
                  <>
                    <span className="status-label">Seleccionado:</span>
                    <span className="status-value">{tplGeneralPendingFile.name}</span>
                    <button className="btn-guardar-config" onClick={handleSaveTplGeneral}>
                      Guardar
                    </button>
                  </>
                )}
                {tplGeneralName && !tplGeneralPendingFile && (
                  <>
                    <span className="status-label">Plantilla activa:</span>
                    <span className="status-value">{tplGeneralName}</span>
                    <span style={{ color: '#10b981', fontWeight: 600 }}>Guardado</span>
                    <button className="btn-restaurar" onClick={handleRemoveTplGeneral}>
                      Quitar
                    </button>
                  </>
                )}
                {!tplGeneralName && !tplGeneralPendingFile && (
                  <small>No hay plantilla seleccionada</small>
                )}
              </div>
            </div>
            <div className="config-item">
              <label>Reporte Pago Vista Administrador (PDF)</label>
              <div className="input-group">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleUploadTplAdmin(e.target.files?.[0] || null)}
                />
              </div>
            <div className="template-status" style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {tplAdminPendingFile && (
                <>
                  <span className="status-label">Seleccionado:</span>
                  <span className="status-value">{tplAdminPendingFile.name}</span>
                  <button className="btn-guardar-config" onClick={handleSaveTplAdmin}>
                    Guardar
                  </button>
                </>
              )}
              {tplAdminName && !tplAdminPendingFile && (
                <>
                  <span className="status-label">Plantilla activa:</span>
                  <span className="status-value">{tplAdminName}</span>
                  <span style={{ color: '#10b981', fontWeight: 600 }}>Guardado</span>
                </>
              )}
              {!tplAdminName && !tplAdminPendingFile && (
                <small>No hay plantilla seleccionada</small>
              )}
            </div>
            </div>
            <div className="config-item">
              <label>Reporte de Pago Colaborador (PDF)</label>
              <div className="input-group">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleUploadTplColab(e.target.files?.[0] || null)}
                />
              </div>
              <div className="template-status" style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {tplColabPendingFile && (
                  <>
                    <span className="status-label">Seleccionado:</span>
                    <span className="status-value">{tplColabPendingFile.name}</span>
                    <button className="btn-guardar-config" onClick={handleSaveTplColab}>
                      Guardar
                    </button>
                  </>
                )}
                {tplColabName && !tplColabPendingFile && (
                  <>
                    <span className="status-label">Plantilla activa:</span>
                    <span className="status-value">{tplColabName}</span>
                    <span style={{ color: '#10b981', fontWeight: 600 }}>Guardado</span>
                    <button className="btn-restaurar" onClick={handleRemoveTplColab}>
                      Quitar
                    </button>
                  </>
                )}
                {!tplColabName && !tplColabPendingFile && (
                  <small>No hay plantilla seleccionada</small>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Resumen de Configuración */}
        <div className="config-summary">
          <FaBusinessTime className="summary-icon" />
          <div className="summary-content">
            <h3>Resumen de Configuración Actual</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Tarifa promedio/hora:</span>
                <span className="summary-value">
                  ${Math.round((config.tarifa_hora_junior + config.tarifa_hora_semi_senior + config.tarifa_hora_senior) / 3).toLocaleString()}
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Costo mínimo proyecto:</span>
                <span className="summary-value">
                  ${(config.horas_minimas_proyecto * config.tarifa_hora_junior).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Configuracion;
